import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme, ACCENTS } from "@/contexts/ThemeContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess, hasFamilyPlan, PLANS } from "@/lib/plans";
import { supabase } from "@/lib/supabase";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { CATEGORY_ICONS, resolveCategoryIcon } from "@/lib/categoryIcons";
import {
    LogOut, User, Coins, Lock, Eye, EyeOff, Palette,
    Sun, Moon, Monitor, Check, Globe, MapPin, Calendar, Tag, Trash2,
    Plus, Camera, SlidersHorizontal, Download, Crown, AlertTriangle,
    Users, Copy, RefreshCw, UserPlus, UserMinus, Shield, Home, Mail, Send, X, Bell, Bot,
} from "lucide-react";
import ConnectedApps from "@/components/ConnectedApps";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function resizeToDataUrl(file) {
    return new Promise(resolve => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            const size = 256;
            const canvas = document.createElement("canvas");
            canvas.width = size; canvas.height = size;
            const ctx = canvas.getContext("2d");
            const min = Math.min(img.width, img.height);
            ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.src = url;
    });
}

const TABS = [
    { id: "profile",     label: "Profile",     icon: User,             desc: "Personal info & password" },
    { id: "appearance",  label: "Appearance",  icon: Palette,          desc: "Theme & accent colors" },
    { id: "preferences", label: "Preferences", icon: SlidersHorizontal, desc: "Currency & categories" },
    { id: "household",   label: "Family",      icon: Users,            desc: "Share with spouse or family" },
    { id: "ai",          label: "AI assistants", icon: Bot,            desc: "Connect Claude, ChatGPT or Gemini" },
    { id: "plan",        label: "Plan",        icon: Crown,            desc: "Free, Pro, or Family" },
];

async function exportToExcel(onDone) {
    const XLSX = (await import("xlsx")).default;
    const [expRes, subsRes, fundsRes, contribRes] = await Promise.all([
        api.get("/expenses", { params: { limit: 2000 } }),
        api.get("/subscriptions"),
        api.get("/funds"),
        api.get("/contributions"),
    ]);
    const expenses = expRes.data.filter(e => e.type === "expense");
    const income   = expRes.data.filter(e => e.type === "income");
    const subs     = subsRes.data;
    const funds    = fundsRes.data;
    const contribs = contribRes.data;
    const fundMap  = Object.fromEntries(funds.map(f => [f.id, f.name]));

    const wb = XLSX.utils.book_new();
    const sheet = (rows, name) => XLSX.utils.book_append_sheet(wb,
        rows.length ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet([["No data"]]), name);

    sheet(expenses.map(e => ({
        Date: e.occurred_at?.slice(0, 10), Title: e.title, Category: e.category,
        Amount: parseFloat(e.amount), "Payment Method": e.payment_method || "", Notes: e.notes || "",
    })), "Expenses");
    sheet(income.map(e => ({
        Date: e.occurred_at?.slice(0, 10), Title: e.title, Category: e.category,
        Amount: parseFloat(e.amount), "Payment Method": e.payment_method || "", Notes: e.notes || "",
    })), "Income");
    sheet(subs.map(s => ({
        Name: s.name, Cost: parseFloat(s.cost), "Billing Cycle": s.billing_cycle,
        "Renewal Date": s.renewal_date, Category: s.category,
        "Payment Method": s.payment_method || "", Status: s.status,
        "Payment Type": s.payment_option, Trial: s.is_trial ? "Yes" : "No",
    })), "Subscriptions");
    sheet(funds.map(f => {
        const goal = parseFloat(f.goal_amount);
        const saved = parseFloat(f.current_amount);
        return {
            Name: f.name, Category: f.category, "Goal Amount": goal, "Saved Amount": saved,
            "Progress %": goal > 0 ? Math.round((saved / goal) * 100) : 0,
            "Due Date": f.due_date || "", Notes: f.notes || "",
        };
    }), "Sinking Funds");
    sheet(contribs.map(c => ({
        Date: c.occurred_at?.slice(0, 10), Fund: fundMap[c.fund_id] || "",
        Type: c.kind, Amount: parseFloat(c.amount), Note: c.note || "",
    })), "Contributions");

    XLSX.writeFile(wb, `budgetwise-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
    onDone?.();
}

export default function Settings() {
    const { user, updateProfile, logout, deleteAccount } = useAuth();
    const { mode, accent, setMode, setAccent, effectiveMode } = useTheme();
    const { categories, refresh: refreshCats } = useCategories();
    const { household, isOwner, isMember, pendingRequest, refresh: refreshHousehold, refreshPendingRequest } = useHousehold();
    // Household members get full Pro access under the owner's plan without their own
    // paid plan — see lib/plans.js's hasProAccess() and CLAUDE.md's "Plans" section.
    const hasPro = hasProAccess(user, isMember);
    // "Create a household" is Family-plan-only, checked on the user's own literal plan
    // (not inherited) — see lib/plans.js's hasFamilyPlan().
    const canCreateHousehold = hasFamilyPlan(user);
    const fileInputRef = useRef(null);
    // Supports deep-linking a specific tab from outside the page (e.g. the profile
    // dropdown's Profile/Preferences/Family/Appearance shortcuts — /settings?tab=household).
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const [activeTab, setActiveTab] = useState(TABS.some(t => t.id === tabParam) ? tabParam : "profile");
    useEffect(() => {
        if (tabParam && TABS.some(t => t.id === tabParam)) setActiveTab(tabParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabParam]);

    const [form, setForm] = useState({
        first_name: "", last_name: "", dob: "", city: "", country: "",
        currency: "USD", avatar_url: "",
    });
    const [saving, setSaving] = useState(false);
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [pwBusy, setPwBusy] = useState(false);
    const [showCur, setShowCur] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [newCatName, setNewCatName] = useState("");
    const [newCatIcon, setNewCatIcon] = useState("shapes");
    const [catAdding, setCatAdding] = useState(false);
    const [catIconSaving, setCatIconSaving] = useState(null); // id of category whose icon is mid-save
    const [exporting, setExporting] = useState(false);
    const [defaultScopeSaving, setDefaultScopeSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");

    const [hhName, setHhName] = useState("");
    const [joinCode, setJoinCode] = useState("");
    const [hhBusy, setHhBusy] = useState(false);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [deleteHhDialogOpen, setDeleteHhDialogOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteBusy, setInviteBusy] = useState(false);
    const [invites, setInvites] = useState([]);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [joinRequests, setJoinRequests] = useState([]);
    const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
    const [decidingId, setDecidingId] = useState(null);
    const [cancelingRequest, setCancelingRequest] = useState(false);

    useEffect(() => {
        if (user) setForm({
            first_name: user.first_name || "", last_name: user.last_name || "",
            dob: user.dob || "", city: user.city || "", country: user.country || "",
            currency: user.currency || "USD", avatar_url: user.avatar_url || "",
        });
    }, [user]);

    const saveProfile = async () => {
        try {
            setSaving(true);
            await updateProfile({
                first_name: form.first_name || null, last_name: form.last_name || null,
                dob: form.dob || null, city: form.city || null, country: form.country || null,
                currency: form.currency, avatar_url: form.avatar_url || null,
            });
            toast.success("Profile saved");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
        finally { setSaving(false); }
    };

    const changePassword = async () => {
        if (!pw.current || !pw.next) { toast.error("Fill all password fields"); return; }
        if (pw.next.length < 6) { toast.error("New password must be 6+ characters"); return; }
        if (pw.next !== pw.confirm) { toast.error("Passwords do not match"); return; }
        try {
            setPwBusy(true);
            // Re-authenticate to verify current password, then update
            const { error: authErr } = await supabase.auth.signInWithPassword({
                email: user.email, password: pw.current,
            });
            if (authErr) { toast.error("Current password is incorrect"); return; }
            const { error } = await supabase.auth.updateUser({ password: pw.next });
            if (error) throw error;
            toast.success("Password changed");
            setPw({ current: "", next: "", confirm: "" });
        } catch (e) { toast.error(e.message || "Failed to change password"); }
        finally { setPwBusy(false); }
    };

    const addCategory = async () => {
        const name = newCatName.trim();
        if (!name) return;
        try {
            setCatAdding(true);
            await api.post("/categories", { name, icon: newCatIcon });
            setNewCatName("");
            setNewCatIcon("shapes");
            await refreshCats();
            toast.success("Category added");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setCatAdding(false); }
    };

    const deleteCategory = async (id) => {
        try {
            await api.delete(`/categories/${id}`);
            await refreshCats();
            toast.success("Removed");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const updateCategoryIcon = async (id, icon) => {
        try {
            setCatIconSaving(id);
            await api.patch(`/categories/${id}`, { icon });
            await refreshCats();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to update icon"); }
        finally { setCatIconSaving(null); }
    };

    // Instant-apply, like the theme/accent pickers — sets which scope (Combined vs Just me)
    // every Combined/Just me page (Dashboard, Expenses, Subscriptions, Sinking Funds,
    // Investments, Transactions) opens with by default.
    const setDefaultScope = async (value) => {
        if (value === user?.default_scope) return;
        try {
            setDefaultScopeSaving(true);
            await updateProfile({ default_scope: value });
            toast.success(`Default view set to ${value === "household" ? "Combined" : "Just me"}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
        finally { setDefaultScopeSaving(false); }
    };

    // Instant-apply toggle for the daily digest — same pattern as setDefaultScope above.
    const [notifSaving, setNotifSaving] = useState(null); // which field key is mid-save, or null
    const toggleNotification = async (field, value) => {
        try {
            setNotifSaving(field);
            await updateProfile({ [field]: value });
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
        finally { setNotifSaving(null); }
    };

    const [testDigestSending, setTestDigestSending] = useState(false);
    const sendTestDigest = async () => {
        try {
            setTestDigestSending(true);
            await api.post("/notifications/test-digest");
            toast.success(`Test email sent to ${user?.email}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to send test email"); }
        finally { setTestDigestSending(false); }
    };

    // Household changes also flip which category pot the user sees, so refresh both.
    const refreshAfterHouseholdChange = () => Promise.all([refreshHousehold(), refreshCats()]);

    const createHousehold = async () => {
        try {
            setHhBusy(true);
            await api.post("/household", { name: hhName.trim() || undefined });
            await refreshAfterHouseholdChange();
            toast.success("Household created");
            setHhName("");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to create household"); }
        finally { setHhBusy(false); }
    };

    const joinHousehold = async () => {
        if (!joinCode.trim()) { toast.error("Enter an invite code"); return; }
        try {
            setHhBusy(true);
            const { data } = await api.post("/household/join", { code: joinCode.trim().toUpperCase() });
            await refreshPendingRequest();
            toast.success(`Request sent to join ${data.household_name} — waiting for approval`);
            setJoinCode("");
        } catch (e) { toast.error(e.response?.data?.detail || "Invalid or expired code"); }
        finally { setHhBusy(false); }
    };

    const cancelJoinRequest = async () => {
        try {
            setCancelingRequest(true);
            await api.delete("/household/join-requests/mine");
            await refreshPendingRequest();
            toast.success("Request cancelled");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setCancelingRequest(false); }
    };

    const leaveHousehold = async () => {
        try {
            setHhBusy(true);
            await api.post("/household/leave");
            await refreshAfterHouseholdChange();
            toast.success("You left the household");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setHhBusy(false); setLeaveDialogOpen(false); }
    };

    const deleteHousehold = async () => {
        try {
            setHhBusy(true);
            await api.delete("/household");
            await refreshAfterHouseholdChange();
            toast.success("Household deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setHhBusy(false); setDeleteHhDialogOpen(false); }
    };

    const removeMember = async () => {
        if (!removeTarget) return;
        try {
            await api.delete(`/household/members/${removeTarget.id}`);
            await refreshHousehold();
            toast.success(`${removeTarget.name} removed`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setRemoveTarget(null); }
    };

    const regenerateCode = async () => {
        try {
            setHhBusy(true);
            await api.post("/household/regenerate-code");
            await refreshHousehold();
            toast.success("New invite code generated");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setHhBusy(false); }
    };

    const copyInviteCode = () => {
        if (!household) return;
        navigator.clipboard.writeText(household.invite_code);
        toast.success("Invite code copied");
    };

    const loadInvites = async () => {
        if (!household || !isOwner) { setInvites([]); return; }
        try {
            setInvitesLoading(true);
            const { data } = await api.get("/household/invites");
            setInvites(data);
        } catch { /* non-fatal — owner-only list, silently ignore */ }
        finally { setInvitesLoading(false); }
    };

    const loadJoinRequests = async () => {
        if (!household || !isOwner) { setJoinRequests([]); return; }
        try {
            setJoinRequestsLoading(true);
            const { data } = await api.get("/household/join-requests");
            setJoinRequests(data);
        } catch { /* non-fatal — owner-only list, silently ignore */ }
        finally { setJoinRequestsLoading(false); }
    };

    useEffect(() => {
        if (activeTab === "household") { loadInvites(); loadJoinRequests(); }
        /* eslint-disable-next-line */
    }, [activeTab, household, isOwner]);

    const approveJoinRequest = async (req) => {
        try {
            setDecidingId(req.id);
            await api.post(`/household/join-requests/${req.id}/approve`);
            setJoinRequests(prev => prev.filter(r => r.id !== req.id));
            await refreshHousehold();
            toast.success(`${req.name} joined the household`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setDecidingId(null); }
    };

    const declineJoinRequest = async (req) => {
        try {
            setDecidingId(req.id);
            await api.post(`/household/join-requests/${req.id}/decline`);
            setJoinRequests(prev => prev.filter(r => r.id !== req.id));
            toast.success(`Declined ${req.name}'s request`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setDecidingId(null); }
    };

    const inviteByEmail = async () => {
        const email = inviteEmail.trim();
        if (!email) { toast.error("Enter an email address"); return; }
        try {
            setInviteBusy(true);
            await api.post("/household/invite", { email, redirect_origin: window.location.origin });
            toast.success(`Invite sent to ${email}`);
            setInviteEmail("");
            await loadInvites();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invite"); }
        finally { setInviteBusy(false); }
    };

    const resendInvite = async (invite) => {
        try {
            setResendingId(invite.id);
            await api.post(`/household/invites/${invite.id}/resend`, { redirect_origin: window.location.origin });
            toast.success(`Invite resent to ${invite.email}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend"); }
        finally { setResendingId(null); }
    };

    const revokeInvite = async (invite) => {
        try {
            await api.delete(`/household/invites/${invite.id}`);
            setInvites(prev => prev.filter(i => i.id !== invite.id));
            toast.success("Invite revoked");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const fullName = `${form.first_name || ""} ${form.last_name || ""}`.trim() || user?.name || "User";
    const initials = (form.first_name?.[0] || user?.name?.[0] || "U").toUpperCase() + (form.last_name?.[0]?.toUpperCase() || "");

    return (
        <div className="space-y-4 max-w-5xl" data-testid="settings-page">
            {/* Mobile tab pills */}
            <div className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`relative flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                            activeTab === t.id ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}>
                        <t.icon className="w-3.5 h-3.5" />{t.label}
                        {t.id === "household" && joinRequests.length > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{joinRequests.length}</span>
                        )}
                    </button>
                ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-5 items-start">

                {/* Sidebar */}
                <aside className="hidden lg:flex flex-col w-56 flex-shrink-0 card-elev rounded-2xl overflow-hidden">
                    {/* Avatar block */}
                    <div className="flex flex-col items-center gap-2 p-5 pb-4">
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                            className="relative w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-slate-100 hover:ring-indigo-300 transition-all group">
                            <Avatar className="w-full h-full rounded-none">
                                <AvatarImage src={form.avatar_url} alt={fullName} className="object-cover" />
                                <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold text-xl w-full h-full rounded-none">{initials}</AvatarFallback>
                            </Avatar>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                <Camera className="w-5 h-5 text-white" />
                            </div>
                        </button>
                        <div className="text-center min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate w-36">{fullName}</p>
                            <p className="text-xs text-slate-400 truncate w-36">{user?.email}</p>
                        </div>
                    </div>

                    {/* Nav items */}
                    <div className="border-t border-slate-100 px-2 py-2 space-y-0.5">
                        {TABS.map(t => (
                            <button key={t.id} onClick={() => setActiveTab(t.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all group ${
                                    activeTab === t.id
                                        ? "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300"
                                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-100"
                                }`}>
                                <t.icon className="w-4 h-4 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold leading-tight">{t.label}</div>
                                    <div className="text-[11px] opacity-60 leading-tight mt-0.5">{t.desc}</div>
                                </div>
                                {t.id === "household" && joinRequests.length > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex-shrink-0">{joinRequests.length}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Sign out */}
                    <div className="border-t border-slate-100 px-2 py-2 mt-auto">
                        <button onClick={logout} data-testid="set-logout"
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-rose-500 hover:bg-rose-50 hover:text-rose-600 transition-all">
                            <LogOut className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-semibold">Sign out</span>
                        </button>
                    </div>
                </aside>

                {/* Hidden file input */}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" data-testid="set-avatar"
                    onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setForm(f => ({ ...f, avatar_url: "__loading__" }));
                        const dataUrl = await resizeToDataUrl(file);
                        setForm(f => ({ ...f, avatar_url: dataUrl }));
                        e.target.value = "";
                    }}
                />

                {/* Content panel */}
                <div className="flex-1 min-w-0 space-y-5">

                    {/* ── PROFILE TAB ── */}
                    {activeTab === "profile" && <>
                        <div className="card-elev rounded-2xl p-6 space-y-5" data-testid="profile-section">
                            <SectionHeader icon={User} color="indigo" title="Profile" desc="Your personal details" />

                            {/* Photo (mobile only — desktop shows in sidebar) */}
                            <div className="flex items-center gap-4 lg:hidden">
                                <button type="button" onClick={() => fileInputRef.current?.click()}
                                    className="relative w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-slate-100 hover:ring-indigo-300 transition-all group flex-shrink-0">
                                    <Avatar className="w-full h-full rounded-none">
                                        <AvatarImage src={form.avatar_url} alt={fullName} className="object-cover" />
                                        <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold text-2xl w-full h-full rounded-none">{initials}</AvatarFallback>
                                    </Avatar>
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                        <Camera className="w-6 h-6 text-white" />
                                    </div>
                                </button>
                                <div>
                                    <p className="text-sm font-semibold text-slate-700">Profile photo</p>
                                    <p className="text-xs text-slate-500 mt-0.5">Click to upload</p>
                                    {form.avatar_url && form.avatar_url !== "__loading__" && (
                                        <button type="button" onClick={() => setForm(f => ({ ...f, avatar_url: "" }))}
                                            className="text-xs text-rose-500 hover:text-rose-600 mt-1">Remove</button>
                                    )}
                                </div>
                            </div>

                            {/* Desktop: show remove link below avatar hint */}
                            {form.avatar_url && form.avatar_url !== "__loading__" && (
                                <div className="hidden lg:flex items-center gap-2 text-xs text-slate-500">
                                    <Camera className="w-3.5 h-3.5" />
                                    <span>Click the photo in the sidebar to change it.</span>
                                    <button type="button" onClick={() => setForm(f => ({ ...f, avatar_url: "" }))}
                                        className="text-rose-500 hover:text-rose-600 font-medium">Remove</button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="First name">
                                    <Input data-testid="set-first" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Alex" />
                                </Field>
                                <Field label="Last name">
                                    <Input data-testid="set-last" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Morgan" />
                                </Field>
                                <Field label="Email">
                                    <Input value={user?.email || ""} disabled className="bg-slate-50 text-slate-500" />
                                </Field>
                                <Field label="Date of birth">
                                    <div className="relative">
                                        <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        <Input data-testid="set-dob" type="date" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} className="pl-9" />
                                    </div>
                                </Field>
                                <Field label="City">
                                    <div className="relative">
                                        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        <Input data-testid="set-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="San Francisco" className="pl-9" />
                                    </div>
                                </Field>
                                <Field label="Country">
                                    <div className="relative">
                                        <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        <Input data-testid="set-country" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="USA" className="pl-9" />
                                    </div>
                                </Field>
                            </div>
                            <div className="flex justify-end pt-1">
                                <Button onClick={saveProfile} disabled={saving} data-testid="set-save"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-6 font-semibold">
                                    {saving ? "Saving…" : "Save changes"}
                                </Button>
                            </div>
                        </div>

                        {/* Change password */}
                        <div className="card-elev rounded-2xl p-6 space-y-5" data-testid="password-section">
                            <SectionHeader icon={Lock} color="rose" title="Change password" desc="Use a strong, unique password" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Current password">
                                    <div className="relative">
                                        <Input data-testid="pw-current" type={showCur ? "text" : "password"}
                                            value={pw.current} onChange={e => setPw({ ...pw, current: e.target.value })}
                                            autoComplete="current-password" className="pr-10" />
                                        <button type="button" onClick={() => setShowCur(!showCur)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </Field>
                                <div />
                                <Field label="New password">
                                    <div className="relative">
                                        <Input data-testid="pw-new" type={showNew ? "text" : "password"}
                                            value={pw.next} onChange={e => setPw({ ...pw, next: e.target.value })}
                                            autoComplete="new-password" placeholder="6+ characters" className="pr-10" />
                                        <button type="button" onClick={() => setShowNew(!showNew)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </Field>
                                <Field label="Confirm new password">
                                    <Input data-testid="pw-confirm" type={showNew ? "text" : "password"}
                                        value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })}
                                        autoComplete="new-password" placeholder="Repeat new password" />
                                </Field>
                            </div>
                            <div className="flex justify-end pt-1">
                                <Button onClick={changePassword} disabled={pwBusy} data-testid="pw-save"
                                    className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl h-10 px-6 font-semibold">
                                    {pwBusy ? "Updating…" : "Update password"}
                                </Button>
                            </div>
                        </div>

                        {/* Export Data */}
                        <div className="card-elev rounded-2xl p-6 space-y-4">
                            <SectionHeader icon={Download} color="indigo" title="Export Data" desc="Download all your financial data as Excel" />
                            {hasPro ? (
                                <div className="space-y-3">
                                    <p className="text-sm text-slate-600">One Excel file with separate sheets for each data type.</p>
                                    <div className="flex flex-wrap gap-2">
                                        {["Expenses","Income","Subscriptions","Sinking Funds","Contributions"].map(s => (
                                            <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700">{s}</span>
                                        ))}
                                    </div>
                                    <Button
                                        onClick={async () => {
                                            setExporting(true);
                                            try { await exportToExcel(() => toast.success("Exported successfully")); }
                                            catch { toast.error("Export failed"); }
                                            finally { setExporting(false); }
                                        }}
                                        disabled={exporting}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-5 gap-2">
                                        <Download className="w-4 h-4" />
                                        {exporting ? "Exporting…" : "Export to Excel (.xlsx)"}
                                    </Button>
                                </div>
                            ) : (
                                <div className="relative rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    {/* Blurred preview — hidden in dark mode (overlay is dark, preview adds noise) */}
                                    <div className="p-4 opacity-20 pointer-events-none select-none space-y-2 dark:invisible">
                                        <div className="flex flex-wrap gap-2">
                                            {["Expenses","Income","Subscriptions","Sinking Funds","Contributions"].map(s => (
                                                <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">{s}</span>
                                            ))}
                                        </div>
                                        <div className="h-9 w-44 bg-indigo-200 dark:bg-indigo-800 rounded-xl" />
                                    </div>
                                    {/* Lock overlay */}
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85 dark:bg-slate-900/90 backdrop-blur-[2px]">
                                        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-800">
                                            <Crown className="w-3.5 h-3.5" /> Premium feature
                                        </span>
                                        <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">Upgrade to export your data</p>
                                        <Button
                                            onClick={() => toast.info("Premium plans coming soon! 🚀")}
                                            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-9 px-5 text-sm gap-1.5 mt-1">
                                            <Crown className="w-3.5 h-3.5" /> Upgrade to Pro
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Danger Zone */}
                        <div className="rounded-2xl border border-rose-300 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/25 p-6 space-y-4">
                            <SectionHeader icon={AlertTriangle} color="rose" title="Danger Zone" desc="Irreversible actions — proceed with caution" />
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900/60">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Delete account</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Permanently delete your account and all associated data. This cannot be undone.</p>
                                </div>
                                <Button
                                    onClick={() => { setDeleteConfirm(""); setDeleteDialogOpen(true); }}
                                    variant="outline"
                                    className="flex-shrink-0 border-rose-400 dark:border-rose-700 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white dark:hover:bg-rose-700 dark:hover:text-white rounded-xl h-9 px-4 text-sm">
                                    Delete account
                                </Button>
                            </div>
                        </div>

                        {/* Delete confirmation dialog */}
                        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                            <AlertDialogContent className="rounded-2xl">
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display text-xl">Delete your account?</AlertDialogTitle>
                                    <AlertDialogDescription className="space-y-2">
                                        <span className="block">This will permanently delete all your expenses, subscriptions, sinking funds, categories, and account data.</span>
                                        <span className="block font-semibold text-slate-700">Type <span className="text-rose-600 font-bold">DELETE</span> to confirm.</span>
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <Input
                                    value={deleteConfirm}
                                    onChange={e => setDeleteConfirm(e.target.value)}
                                    placeholder="Type DELETE to confirm"
                                    className="rounded-xl border-rose-200 focus-visible:ring-rose-400"
                                />
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={async () => {
                                            try { await deleteAccount(); }
                                            catch { toast.error("Failed to delete account"); }
                                        }}
                                        disabled={deleteConfirm !== "DELETE"}
                                        className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl disabled:opacity-40">
                                        Delete my account
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        {/* Mobile sign-out */}
                        <div className="lg:hidden flex items-center justify-between card-elev rounded-2xl p-4">
                            <div className="text-xs text-slate-500">Signed in as <span className="font-semibold text-slate-700">{user?.email}</span></div>
                            <Button onClick={logout} variant="outline" data-testid="set-logout"
                                className="rounded-xl h-9 text-rose-600 hover:bg-rose-50 border-rose-200 text-sm">
                                <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
                            </Button>
                        </div>
                    </>}

                    {/* ── APPEARANCE TAB ── */}
                    {activeTab === "appearance" && (
                        <div className="card-elev rounded-2xl p-6 space-y-6" data-testid="appearance-section">
                            <SectionHeader icon={Palette} color="indigo" title="Appearance" desc="Theme mode and accent color" />

                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-slate-700">Theme mode</Label>
                                <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100 rounded-xl" data-testid="mode-picker">
                                    {[
                                        { id: "light",  icon: Sun,     label: "Light" },
                                        { id: "dark",   icon: Moon,    label: "Dark" },
                                        { id: "system", icon: Monitor, label: "System" },
                                    ].map(({ id, icon: Icon, label }) => (
                                        <button key={id} onClick={() => setMode(id)} data-testid={`mode-${id}`}
                                            className={`flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold transition-all ${
                                                mode === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
                                            }`}>
                                            <Icon className="w-4 h-4" />{label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-500 pt-1">
                                    Currently using <span className="font-semibold text-slate-700">{effectiveMode}</span> mode.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-slate-700">Accent color</Label>
                                <div className="flex flex-wrap gap-3" data-testid="accent-picker">
                                    {ACCENTS.map(a => (
                                        <button key={a.id} onClick={() => setAccent(a.id)} data-testid={`accent-${a.id}`}
                                            title={a.label}
                                            className={`relative w-10 h-10 rounded-xl transition-all active:scale-95 ${
                                                accent === a.id ? "ring-2 ring-offset-2 ring-offset-white scale-110" : "hover:scale-105"
                                            }`}
                                            style={{ backgroundColor: a.hex, boxShadow: accent === a.id ? `0 0 0 2px ${a.hex}` : undefined }}>
                                            {accent === a.id && <Check className="w-4 h-4 text-white absolute inset-0 m-auto" />}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-500 pt-1">Applies across buttons, links, and active states.</p>
                            </div>
                        </div>
                    )}

                    {/* ── PREFERENCES TAB ── */}
                    {activeTab === "preferences" && <>
                        <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="prefs-section">
                            <SectionHeader icon={Coins} color="emerald" title="Currency" desc="Default currency for all amounts" />
                            <Field label="Default currency">
                                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                                    <SelectTrigger data-testid="set-currency" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="USD">USD ($) — US Dollar</SelectItem>
                                        <SelectItem value="INR">INR (₹) — Indian Rupee</SelectItem>
                                        <SelectItem value="EUR">EUR (€) — Euro</SelectItem>
                                        <SelectItem value="GBP">GBP (£) — British Pound</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            <div className="flex justify-end pt-1">
                                <Button onClick={saveProfile} disabled={saving}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-6 font-semibold">
                                    {saving ? "Saving…" : "Save"}
                                </Button>
                            </div>
                        </div>

                        <div className="card-elev rounded-2xl p-6 space-y-1" data-testid="notifications-section">
                            <SectionHeader icon={Bell} color="indigo" title="Email notifications" desc="A once-daily digest of what needs your attention — off by default" />

                            <div className="flex items-center justify-between py-3 border-b border-slate-100">
                                <div className="min-w-0 pr-4">
                                    <p className="text-sm font-semibold text-slate-900">Daily digest emails</p>
                                    <p className="text-xs text-slate-500 mt-0.5">Master switch — turn this on to receive any of the reminders below</p>
                                </div>
                                <Switch
                                    checked={!!user?.email_notifications_enabled}
                                    disabled={notifSaving === "email_notifications_enabled"}
                                    onCheckedChange={v => toggleNotification("email_notifications_enabled", v)}
                                    data-testid="notif-master-toggle"
                                />
                            </div>

                            <div className={`space-y-0.5 transition-opacity ${user?.email_notifications_enabled ? "" : "opacity-40 pointer-events-none"}`}>
                                {[
                                    { key: "notify_subscription_renewals", label: "Subscription renewals", desc: "3 days before a subscription renews", testid: "notif-subs" },
                                    { key: "notify_recurring_bills", label: "Recurring bills", desc: "3 days before due, and daily while overdue", testid: "notif-bills" },
                                    { key: "notify_budget_alerts", label: "Budget alerts", desc: "A category budget crosses 90% of its limit", testid: "notif-budgets" },
                                    { key: "notify_fund_due_dates", label: "Sinking fund due dates", desc: "3 days before due, if still short of the goal", testid: "notif-funds" },
                                ].map(row => (
                                    <div key={row.key} className="flex items-center justify-between py-2.5">
                                        <div className="min-w-0 pr-4">
                                            <p className="text-sm font-medium text-slate-800">{row.label}</p>
                                            <p className="text-xs text-slate-400 mt-0.5">{row.desc}</p>
                                        </div>
                                        <Switch
                                            checked={!!user?.[row.key]}
                                            disabled={notifSaving === row.key}
                                            onCheckedChange={v => toggleNotification(row.key, v)}
                                            data-testid={row.testid}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="pt-4 mt-2 border-t border-slate-100">
                                <Button onClick={sendTestDigest} disabled={testDigestSending} variant="outline"
                                    data-testid="notif-send-test" className="rounded-xl h-9 gap-1.5 text-sm">
                                    <Send className="w-3.5 h-3.5" />
                                    {testDigestSending ? "Sending…" : "Send me a test email"}
                                </Button>
                            </div>
                        </div>

                        {isMember && (
                            <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="default-scope-section">
                                <SectionHeader icon={Users} color="indigo" title="Default view" desc="Which view Dashboard, Expenses, Subscriptions, Sinking Funds, Investments, and Transactions open with" />
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button" data-testid="default-scope-household" disabled={defaultScopeSaving}
                                        onClick={() => setDefaultScope("household")}
                                        className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all disabled:opacity-50 ${
                                            user?.default_scope === "household"
                                                ? "border-indigo-600 bg-indigo-50"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        <span className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${user?.default_scope === "household" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                                            <Users className="w-4 h-4" />
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-900">Combined</div>
                                            <div className="text-xs text-slate-500">Whole household</div>
                                        </div>
                                    </button>
                                    <button
                                        type="button" data-testid="default-scope-mine" disabled={defaultScopeSaving}
                                        onClick={() => setDefaultScope("mine")}
                                        className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all disabled:opacity-50 ${
                                            (user?.default_scope || "mine") === "mine"
                                                ? "border-indigo-600 bg-indigo-50"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        <span className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${(user?.default_scope || "mine") === "mine" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                                            <User className="w-4 h-4" />
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-900">Just me</div>
                                            <div className="text-xs text-slate-500">Only your own data</div>
                                        </div>
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500">You can still switch views on any page — this just sets what it opens with.</p>
                            </div>
                        )}

                        <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="categories-section">
                            <SectionHeader icon={Tag} color="amber" title="Categories" desc="Shared across expenses, subscriptions, and goals — click an icon to change it" />
                            <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-80 overflow-y-auto">
                                {categories.length === 0 && (
                                    <div className="px-4 py-3 text-sm text-slate-400">No categories yet.</div>
                                )}
                                {categories.map(cat => {
                                    const { icon: CatIcon, color: catColor } = resolveCategoryIcon(cat);
                                    return (
                                        <div key={cat.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60 transition-colors">
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <button
                                                        data-testid={`cat-icon-trigger-${cat.id}`}
                                                        disabled={catIconSaving === cat.id}
                                                        title="Change icon"
                                                        style={{ backgroundColor: `${catColor}1a`, color: catColor }}
                                                        className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 hover:brightness-95 transition-all disabled:opacity-50"
                                                    >
                                                        <CatIcon className="w-4 h-4" />
                                                    </button>
                                                </PopoverTrigger>
                                                <PopoverContent align="start" className="w-64">
                                                    <p className="text-xs font-semibold text-slate-700 mb-2">Choose an icon</p>
                                                    <IconPickerGrid
                                                        value={cat.icon}
                                                        disabled={catIconSaving === cat.id}
                                                        onSelect={(icon) => updateCategoryIcon(cat.id, icon)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                            <span className="text-sm text-slate-700 flex-1">{cat.name}</span>
                                            {cat.is_default
                                                ? <Lock className="w-3.5 h-3.5 text-slate-300" />
                                                : <button onClick={() => deleteCategory(cat.id)}
                                                    className="p-1 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                  </button>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button type="button" title="Pick an icon"
                                            style={(() => { const { color } = resolveCategoryIcon({ icon: newCatIcon }); return { backgroundColor: `${color}1a`, color }; })()}
                                            className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 hover:brightness-95 transition-all">
                                            {(() => { const { icon: NewIcon } = resolveCategoryIcon({ icon: newCatIcon }); return <NewIcon className="w-4 h-4" />; })()}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="w-64">
                                        <p className="text-xs font-semibold text-slate-700 mb-2">Choose an icon</p>
                                        <IconPickerGrid value={newCatIcon} onSelect={setNewCatIcon} />
                                    </PopoverContent>
                                </Popover>
                                <Input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && addCategory()}
                                    placeholder="New category name…" className="h-10 rounded-xl flex-1" />
                                <Button onClick={addCategory} disabled={catAdding || !newCatName.trim()}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-4 gap-1.5">
                                    <Plus className="w-4 h-4" /> Add
                                </Button>
                            </div>
                        </div>
                    </>}

                    {/* ── FAMILY / HOUSEHOLD TAB ── */}
                    {activeTab === "household" && <>
                        {household ? (
                            <div className="card-elev rounded-2xl p-6 space-y-5" data-testid="household-section">
                                <SectionHeader icon={Users} color="indigo" title={household.name} desc={`${household.members.length} of 5 members`} />

                                <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Invite code</p>
                                        <p className="font-display text-sm font-extrabold tracking-widest text-slate-900" data-testid="hh-invite-code">{household.invite_code}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" onClick={copyInviteCode} data-testid="hh-copy-code" className="rounded-xl gap-1.5 h-9">
                                            <Copy className="w-3.5 h-3.5" /> Copy
                                        </Button>
                                        {isOwner && (
                                            <Button variant="outline" onClick={regenerateCode} disabled={hhBusy} data-testid="hh-regen-code" className="rounded-xl gap-1.5 h-9">
                                                <RefreshCw className="w-3.5 h-3.5" /> New code
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 -mt-3">Share this code with your spouse or family — they sign up for their own login, enter it here to request to join, and you approve them below.</p>

                                {isOwner && joinRequests.length > 0 && (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="hh-join-requests">
                                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Waiting for your approval ({joinRequests.length})</p>
                                        {joinRequests.map(req => (
                                            <div key={req.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white" data-testid={`hh-join-request-${req.id}`}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Avatar className="w-7 h-7 flex-shrink-0">
                                                        <AvatarImage src={req.avatar_url} alt={req.name} />
                                                        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{req.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                                                    </Avatar>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-slate-900 truncate">{req.name}</p>
                                                        <p className="text-xs text-slate-500 truncate">{req.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    <Button size="sm" onClick={() => approveJoinRequest(req)} disabled={decidingId === req.id}
                                                        data-testid={`hh-join-approve-${req.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-8 px-3 text-xs">
                                                        Approve
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => declineJoinRequest(req)} disabled={decidingId === req.id}
                                                        data-testid={`hh-join-decline-${req.id}`} className="rounded-lg h-8 px-3 text-xs">
                                                        Decline
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {isOwner && joinRequestsLoading && joinRequests.length === 0 && (
                                    <p className="text-xs text-slate-400">Checking for join requests…</p>
                                )}

                                {isOwner && (
                                    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="hh-invite-email-section">
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Or invite by email</p>
                                        <div className="flex gap-2">
                                            <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                                                onKeyDown={e => e.key === "Enter" && inviteByEmail()}
                                                placeholder="family@example.com" data-testid="hh-invite-email-input"
                                                className="h-10 rounded-xl flex-1" />
                                            <Button onClick={inviteByEmail} disabled={inviteBusy || !inviteEmail.trim()} data-testid="hh-invite-email-btn"
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-4 gap-1.5">
                                                <Send className="w-3.5 h-3.5" /> Send invite
                                            </Button>
                                        </div>
                                        <p className="text-xs text-slate-400">They'll get an email with a link to set a password and join automatically.</p>

                                        {invites.length > 0 && (
                                            <div className="pt-2 space-y-1.5" data-testid="hh-pending-invites">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pending ({invites.length})</p>
                                                {invites.map(inv => (
                                                    <div key={inv.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50" data-testid={`hh-invite-${inv.id}`}>
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                            <span className="text-sm text-slate-700 truncate">{inv.email}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <button onClick={() => resendInvite(inv)} disabled={resendingId === inv.id} data-testid={`hh-invite-resend-${inv.id}`}
                                                                className="p-1.5 rounded-lg text-slate-400 hover:bg-white hover:text-indigo-600" title="Resend">
                                                                <RefreshCw className={`w-3.5 h-3.5 ${resendingId === inv.id ? "animate-spin" : ""}`} />
                                                            </button>
                                                            <button onClick={() => revokeInvite(inv)} data-testid={`hh-invite-revoke-${inv.id}`}
                                                                className="p-1.5 rounded-lg text-slate-400 hover:bg-white hover:text-rose-500" title="Revoke">
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {invitesLoading && invites.length === 0 && (
                                            <p className="text-xs text-slate-400">Loading invites…</p>
                                        )}
                                    </div>
                                )}

                                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="hh-members">
                                    {household.members.map(m => (
                                        <div key={m.id} className="flex items-center justify-between px-4 py-3" data-testid={`hh-member-${m.id}`}>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <Avatar className="w-8 h-8 flex-shrink-0">
                                                    <AvatarImage src={m.avatar_url} alt={m.name} />
                                                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{m.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                                                </Avatar>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-slate-900 truncate">{m.name}{m.id === user.id ? " (you)" : ""}</p>
                                                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {m.is_owner ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                                                        <Shield className="w-3 h-3" /> Owner
                                                    </span>
                                                ) : isOwner ? (
                                                    <button onClick={() => setRemoveTarget(m)} data-testid={`hh-remove-${m.id}`} className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500">
                                                        <UserMinus className="w-4 h-4" />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-end pt-1">
                                    {isOwner ? (
                                        <Button variant="outline" onClick={() => setDeleteHhDialogOpen(true)} data-testid="hh-delete-btn"
                                            className="rounded-xl h-9 text-rose-600 border-rose-200 hover:bg-rose-50">
                                            Delete household
                                        </Button>
                                    ) : (
                                        <Button variant="outline" onClick={() => setLeaveDialogOpen(true)} data-testid="hh-leave-btn"
                                            className="rounded-xl h-9 text-rose-600 border-rose-200 hover:bg-rose-50">
                                            Leave household
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {pendingRequest ? (
                                    <div className="card-elev rounded-2xl p-6 space-y-3" data-testid="household-pending-section">
                                        <SectionHeader icon={UserPlus} color="amber" title={`Request sent to ${pendingRequest.household_name}`} desc="Waiting for the household owner to approve you. This checks automatically every 20s." />
                                        <div className="flex gap-2">
                                            <Button variant="outline" onClick={refreshHousehold} data-testid="hh-check-status-btn" className="rounded-xl h-9">
                                                <RefreshCw className="w-3.5 h-3.5" /> Check now
                                            </Button>
                                            <Button variant="outline" onClick={cancelJoinRequest} disabled={cancelingRequest} data-testid="hh-cancel-request-btn"
                                                className="rounded-xl h-9 text-rose-600 border-rose-200 hover:bg-rose-50">
                                                Cancel request
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="household-join-section">
                                        <SectionHeader icon={UserPlus} color="emerald" title="Join a household" desc="Have an invite code from a family member?" />
                                        <div className="flex gap-2">
                                            <Input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
                                                onKeyDown={e => e.key === "Enter" && joinHousehold()}
                                                placeholder="e.g. FLOW8X2" maxLength={12} data-testid="hh-join-input"
                                                className="h-10 rounded-xl flex-1 tracking-widest font-semibold uppercase" />
                                            <Button onClick={joinHousehold} disabled={hhBusy || !joinCode.trim()} data-testid="hh-join-btn"
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-10 px-5">
                                                Request to join
                                            </Button>
                                        </div>
                                        <p className="text-xs text-slate-400">The household owner will need to approve your request before you get access.</p>
                                    </div>
                                )}

                                {canCreateHousehold ? (
                                    <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="household-create-section">
                                        <SectionHeader icon={Home} color="indigo" title="Create a household" desc="Share your account with a spouse or family" />
                                        <div className="space-y-3">
                                            <Field label="Household name (optional)">
                                                <Input value={hhName} onChange={e => setHhName(e.target.value)} placeholder="The Smiths"
                                                    data-testid="hh-name-input" className="h-10 rounded-xl" />
                                            </Field>
                                            <Button onClick={createHousehold} disabled={hhBusy} data-testid="hh-create-btn"
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-6">
                                                Create household
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card-elev rounded-2xl p-6 space-y-3 text-center" data-testid="household-upsell-section">
                                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 grid place-items-center mx-auto shadow-sm shadow-amber-500/20">
                                            <Lock className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-display text-base font-bold text-slate-900">Family sharing is a Family plan feature</h3>
                                            <p className="text-sm text-slate-500 mt-1">Upgrade to Family (${PLANS.family.price}/mo) to create a household and share tracking with up to 5 people.</p>
                                        </div>
                                        <Link to="/pricing" data-testid="hh-upgrade-btn"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold shadow-sm shadow-amber-500/20 transition-all">
                                            <Crown className="w-4 h-4" /> View plans
                                        </Link>
                                    </div>
                                )}
                            </>
                        )}

                        <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
                            <AlertDialogContent className="rounded-2xl">
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display text-xl">Leave this household?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Your subscriptions, sinking funds, and investments stay yours. You'll stop seeing the shared expenses, categories, and budgets — and they'll stop seeing yours.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={leaveHousehold} disabled={hhBusy} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                                        Leave household
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog open={deleteHhDialogOpen} onOpenChange={setDeleteHhDialogOpen}>
                            <AlertDialogContent className="rounded-2xl">
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display text-xl">Delete this household?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Every member is removed and the shared categories/budgets/expenses become theirs individually again. This cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={deleteHousehold} disabled={hhBusy} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                                        Delete household
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
                            <AlertDialogContent className="rounded-2xl">
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display text-xl">Remove {removeTarget?.name}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        They'll keep their own subscriptions, funds, and investments, but lose access to the shared household data.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={removeMember} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                                        Remove
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </>}

                    {activeTab === "ai" && <ConnectedApps canUse={hasProAccess(user, isMember)} />}

                    {activeTab === "plan" && <>
                        <div className="card-elev rounded-2xl p-6 space-y-4" data-testid="plan-section">
                            <SectionHeader icon={Crown} color="amber" title="Your plan" desc="Manage your FlowBudget subscription" />
                            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current plan</div>
                                    <div className="font-display text-lg font-bold text-slate-900 mt-0.5" data-testid="current-plan-label">
                                        {PLANS[user?.plan]?.label || "Free"}
                                        {isMember && user?.plan !== "family" && <span className="text-slate-400 font-normal text-sm"> (via household)</span>}
                                    </div>
                                </div>
                                <Link to="/pricing" data-testid="plan-manage-btn"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all">
                                    {user?.plan === "family" ? "Manage plan" : "Upgrade"}
                                </Link>
                            </div>
                            <p className="text-xs text-slate-400">
                                No real payment is processed yet — plan changes take effect immediately for testing purposes.
                            </p>
                        </div>
                    </>}

                </div>
            </div>
        </div>
    );
}

function SectionHeader({ icon: Icon, color, title, desc }) {
    const colors = {
        indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400",
        rose:   "bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400",
        emerald:"bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
        amber:  "bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400",
    };
    return (
        <div className="flex items-center gap-3">
            <span className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${colors[color]}`}>
                <Icon className="w-4 h-4" />
            </span>
            <div>
                <h2 className="font-display text-base font-bold text-slate-900">{title}</h2>
                <p className="text-xs text-slate-500">{desc}</p>
            </div>
        </div>
    );
}

// Grid of pickable category icons — mirrors the accent-color swatch picker's
// selected-ring + Check overlay pattern, one button per icon in CATEGORY_ICONS.
function IconPickerGrid({ value, onSelect, disabled }) {
    return (
        <div className="grid grid-cols-6 gap-1.5 max-h-56 overflow-y-auto pr-1">
            {CATEGORY_ICONS.map(({ value: v, label, icon: Icon, color }) => (
                <button
                    key={v} type="button" title={label} disabled={disabled}
                    onClick={() => onSelect(v)}
                    style={{
                        backgroundColor: value === v ? color : `${color}18`,
                        color: value === v ? "#fff" : color,
                        boxShadow: value === v ? `0 0 0 2px ${color}` : undefined,
                    }}
                    className="relative w-9 h-9 rounded-lg grid place-items-center transition-all active:scale-95 disabled:opacity-50 ring-offset-2 ring-offset-white hover:brightness-95"
                >
                    <Icon className="w-4 h-4" />
                </button>
            ))}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
